import React from 'react'
import { useTranslation } from 'react-i18next'

import { formatAmount } from '../../../lib/numbers'
import styles from './sponsor-pledge-flow.module.scss'
import ConfirmationView from './components/ConfirmationView'
import FlowShell from './components/FlowShell'
import SponsorPledgeStripeModal from './components/SponsorPledgeStripeModal'
import Step1Commitment from './components/CommitmentPage'
import Step2Allocation from './components/AllocationPage'
import Step3Information from './components/InformationPage'
import Step4ReviewSubmit from './components/ReviewSubmitPage'
import { buildSponsorPledgeStepperItems } from './sponsorPledgeStepper'
import { useSponsorPledgeWizardState } from './useSponsorPledgeWizardState'

export default function SponsorPledgeWizard({
  event,
  minimumPledgeCiv,
  business,
  currentUser,
  platformFees,
  createTmpSponsorshipPledge,
  updateBusiness,
}) {
  const { t } = useTranslation()
  const w = useSponsorPledgeWizardState({
    event,
    minimumPledgeCiv,
    business,
    currentUser,
    platformFees,
    t,
    createTmpSponsorshipPledge,
    updateBusiness,
  })

  if (w.phase === 'submitted') {
    return (
      <FlowShell
        styles={styles}
        t={t}
        eventBasePath={w.eventBasePath}
        thumbSrc={w.thumbSrc}
        eventTitle={w.eventTitle}
        formattedLocation={w.formattedLocation}
        event={event}
        organizerName={w.organizerName}
        volunteerCount={w.volunteerCount}
        estimatedVolunteerHours={w.estimatedVolunteerHours}
        showStepper={false}
      >
        <ConfirmationView
          styles={styles}
          t={t}
          eventTitle={w.eventTitle}
          formattedLocation={w.formattedLocation}
          totalDue={formatAmount(w.totalDue, true)}
          paymentDeadlineStr={w.paymentDeadlineStr}
          eventBasePath={w.eventBasePath}
        />
      </FlowShell>
    )
  }

  return (
    <>
      <FlowShell
        styles={styles}
        t={t}
        eventBasePath={w.eventBasePath}
        thumbSrc={w.thumbSrc}
        eventTitle={w.eventTitle}
        formattedLocation={w.formattedLocation}
        event={event}
        organizerName={w.organizerName}
        volunteerCount={w.volunteerCount}
        estimatedVolunteerHours={w.estimatedVolunteerHours}
        showStepper
        stepperItems={buildSponsorPledgeStepperItems(
          styles,
          t,
          w.step,
          w.phase,
        )}
      >
        {w.step === 1 ? (
          <Step1Commitment
            styles={styles}
            t={t}
            minPledge={w.minPledge}
            meetsMin={w.meetsMin}
            commitmentNum={w.commitmentNum}
            commitmentInput={w.commitmentInput}
            setCommitmentInput={w.setCommitmentInput}
            spinCommitment={w.spinCommitment}
            onEnterStep2={w.onEnterStep2}
          />
        ) : null}

        {w.step === 2 ? (
          <Step2Allocation
            styles={styles}
            t={t}
            commitmentNum={w.commitmentNum}
            sliderPct={w.sliderPct}
            onSlider={w.onSlider}
            allocWarn={w.allocWarn}
            vpfAmount={w.vpfAmount}
            mpfAmount={w.mpfAmount}
            onVpfInput={w.onVpfInput}
            onMpfInput={w.onMpfInput}
            vpfInputDraft={w.vpfInputDraft}
            mpfInputDraft={w.mpfInputDraft}
            setIsEditingVpf={w.setIsEditingVpf}
            setIsEditingMpf={w.setIsEditingMpf}
            setVpfInputDraft={w.setVpfInputDraft}
            setMpfInputDraft={w.setMpfInputDraft}
            volunteerCount={w.volunteerCount}
            estimatedVolunteerHours={w.estimatedVolunteerHours}
            vpfVolunteerShare={w.vpfVolunteerShare}
            vpfOrgShare={w.vpfOrgShare}
            setStep={w.setStep}
          />
        ) : null}

        {w.step === 3 ? (
          <Step3Information
            styles={styles}
            t={t}
            fieldErrors={w.fieldErrors}
            bizName={w.bizName}
            setBizName={w.setBizName}
            contactName={w.contactName}
            setContactName={w.setContactName}
            bizEmail={w.bizEmail}
            setBizEmail={w.setBizEmail}
            bizPhone={w.bizPhone}
            setBizPhone={w.setBizPhone}
            bizWeb={w.bizWeb}
            setBizWeb={w.setBizWeb}
            orgMessage={w.orgMessage}
            setOrgMessage={w.setOrgMessage}
            validateStep3={w.validateStep3}
            setStep={w.setStep}
          />
        ) : null}

        {w.step === 4 ? (
          <Step4ReviewSubmit
            styles={styles}
            t={t}
            eventTitle={w.eventTitle}
            formattedLocation={w.formattedLocation}
            volunteerCount={w.volunteerCount}
            estimatedVolunteerHours={w.estimatedVolunteerHours}
            applicationDateStr={w.applicationDateStr}
            commitmentNum={w.commitmentNum}
            vpfAmount={w.vpfAmount}
            mpfAmount={w.mpfAmount}
            reviewSubtotal={w.reviewSubtotal}
            paymentDeadlineStr={w.paymentDeadlineStr}
            bizName={w.bizName}
            contactName={w.contactName}
            bizPhone={w.bizPhone}
            bizWeb={w.bizWeb}
            orgMessage={w.orgMessage}
            serviceAmt={w.serviceAmt}
            serviceFeePct={w.serviceFeePct}
            stripeFee={w.stripeFee}
            stripePaymentType={w.stripePaymentType}
            setStripePaymentType={w.setStripePaymentType}
            totalDue={w.totalDue}
            tosAccepted={w.tosAccepted}
            setTosAccepted={w.setTosAccepted}
            openStripeModal={() => w.setStripeModalOpen(true)}
            setStep={w.setStep}
          />
        ) : null}
      </FlowShell>
      <SponsorPledgeStripeModal
        show={w.stripeModalOpen}
        onHide={() => w.setStripeModalOpen(false)}
        onPaymentSuccess={w.completePledgeSubmit}
        submitting={w.submittingPledge}
        paymentMethodType={w.stripePaymentType}
      />
    </>
  )
}
